/* eslint-disable max-lines */
/* eslint-disable no-nested-ternary */
/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable max-params */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable arrow-body-style */
import type { DSHookFloatingContextT } from '../react-desc-prop-types.js';
import { getExpandedFallbackPlacements } from './getExpandedFallbackPlacements.js';
import { getArrowOffsetDynamic } from './getArrowOffset.js';
import { detectOverflow } from './detectOverflow.js';
import {
  applyShift,
  adjustForFixedParent,
  expandWithVariations,
  fits,
  getClippingParent,
  getOverflowScore,
  getOffsetParentData,
  getViewportRect,
  type RectLike,
  type OverflowOffsets,
} from './floatingPositioning.js';

interface ComputePositionProps {
  reference: Element;
  floating: HTMLElement;
  placement: DSHookFloatingContextT.PopperPlacementsT;
  placementOrderPreference?: DSHookFloatingContextT.PopperPlacementsT[];
  customOffset: [number, number];
  withoutPortal: boolean;
}

export const computePosition = (props: ComputePositionProps) => {
  const { reference, floating, placement, placementOrderPreference, customOffset, withoutPortal } = props;

  // When WITHOUT portal: only apply fixed-parent offsets (absolute parents scroll and must NOT be treated as fixed)
  const parentOffsets = withoutPortal ? adjustForFixedParent(reference) : { top: 0, left: 0 };

  const referenceRect = reference.getBoundingClientRect();
  const floatingRect = floating.getBoundingClientRect();

  const fallbackPlacements = placementOrderPreference || getExpandedFallbackPlacements(placement);

  const placements = expandWithVariations(
    [placement].concat(fallbackPlacements as DSHookFloatingContextT.PopperPlacementsT[]),
  );

  // Boundary selection:
  // - Portal => viewport (inset by body padding for Storybook)
  // - No portal => nearest clipping/scroll container (fallback viewport rect)
  const clippingParent = withoutPortal ? getClippingParent(reference) : null;
  const clippingRect: RectLike = clippingParent ? clippingParent.getBoundingClientRect() : getViewportRect();

  // Best-fit selection:
  // 1) choose first placement that fully fits
  // 2) otherwise choose placement with smallest max overflow, tie-break by total overflow
  let bestPlacement = placement;
  let bestOverflows: OverflowOffsets | null = null;
  let bestScore = { total: Number.POSITIVE_INFINITY, maxSide: Number.POSITIVE_INFINITY };

  for (let i = 0; i < placements.length; i += 1) {
    const currentPlacement = placements[i];

    const overflows = detectOverflow(referenceRect, floatingRect, currentPlacement, customOffset, clippingRect);

    if (fits(overflows)) {
      bestPlacement = currentPlacement;
      bestOverflows = overflows;
      break;
    }

    const score = getOverflowScore(overflows);

    const isBetter =
      score.maxSide < bestScore.maxSide || (score.maxSide === bestScore.maxSide && score.total < bestScore.total);

    if (isBetter) {
      bestPlacement = currentPlacement;
      bestOverflows = overflows;
      bestScore = score;
    }
  }

  const finalPlacement = bestPlacement;

  const overflows =
    bestOverflows ?? detectOverflow(referenceRect, floatingRect, finalPlacement, customOffset, clippingRect);

  // Convert overflow -> coordinates.
  // detectOverflow uses viewport/clipping-rect coordinates.
  //
  // - If tooltip is rendered IN A PORTAL (withoutPortal === false) and is positioned with `position: absolute`,
  //   convert viewport coords to page coords by adding window.scrollX/Y.
  //
  // - If tooltip is rendered WITHOUT portal, convert viewport coords to offsetParent coords
  //   (subtract offsetParent rect, add its scroll).
  let x = -overflows.left - parentOffsets.left;
  let y = -overflows.top - parentOffsets.top;

  if (!withoutPortal) {
    x += window.scrollX;
    y += window.scrollY;
  } else {
    const op = getOffsetParentData(floating);
    x = x - op.left + op.scrollLeft;
    y = y - op.top + op.scrollTop;
  }

  // Always shift back inside boundary
  ({ x, y } = applyShift(x, y, overflows));

  const coordsArrow = getArrowOffsetDynamic({
    placement: finalPlacement,
    referenceRect,
    floatingRect,
    x,
    y,
    withoutPortal,
    parentOffsets,
    floatingEl: floating,
    arrowPadding: 12,
  });

  return {
    coordsStyle: {
      transform: `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`,
      top: 0,
      left: 0,
    },
    finalPlacement,
    coordsArrow,
  };
};
